// ============================================================
// VCS.WebRTC — PeerConnection, Offer/Answer, ICE, Opus-Tuning
// ============================================================
window.VCS = window.VCS || {};

VCS.WebRTC = class {

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        // View-Cache
        this._view = {
            localVideo:       $('#localVideo'),
            remoteVideo:      $('#remoteVideo'),
            remoteName:       $('#remoteName'),
            remoteCamOffName: $('#remoteCamOffName'),
            remoteCamOff:     $('#remoteCamOff'),
            remoteMuteIndicator: $('#remoteMuteIndicator'),
            previewVideo:     $('#previewVideo'),
            connectionDot:    $('#connectionDot'),
            connectionText:   $('#connectionText'),
        };
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    // Call starten (isInitiator = true → Guest erstellt Offer)
    async startCall(isInitiator) {
        const s = VCS.state;

        VCS.audioMonitor?.stopPreview();
        VCS.nav?.showScreen('callScreen');
        VCS.captions?.clearTranslated();
        VCS.captions?.clearSelfTranscript();
        s.captionPaused = false;

        // Audio-Fader: reset to default (100 % original) on every call
        // start. No persistence across calls -- founder decision 2026-05-09.
        VCS.audioFader?.onCallStart();

        // Captions-Status aus Chat-Übersetzung ableiten. Beim Gast-Anruf gibt es
        // keinen Chat und damit keine E2E-Sperre: Untertitel sind dort auf
        // beiden Seiten an, ob uebersetzt wird, entscheidet der Server.
        if (s.guestCallActive) {
            s.captionsEnabled = true;
        } else {
            let _captionChatId = s.chatActiveChatId;
            if (!_captionChatId && s.directCallPeerPhone) {
                const chat = s.chatChats.find(c => VCS.chat?.peerPhone(c) === s.directCallPeerPhone);
                if (chat) {
                    _captionChatId = chat.id;
                }
            }
            s.captionsEnabled = _captionChatId ? !s.chatE2EEnabled[_captionChatId] : false;
        }
        VCS.captions?.sendConfig();
        VCS.captions?.updateFabUI();

        // Lokales Video im kleinen Overlay
        this._view.localVideo.prop('srcObject', s.localStream);
        this._view.remoteName.text(s.peerName);
        // .text() ersetzt den Inhalt der Pill komplett -- den Mitschrift-Punkt
        // danach wieder anhaengen, sonst verschwindet er beim Namenswechsel.
        VCS.callControls?.updateSummaryDot?.();
        this._view.remoteCamOffName.text(s.peerName);

        // Mute/Cam-Button-Status synchronisieren
        VCS.callControls?.updateMuteButton();
        VCS.callControls?.updateCamButton();
        VCS.ui?.updateBlurButtonUI();
        VCS.blur?._applyBlurMode();

        // PeerConnection erstellen
        s.pc = new RTCPeerConnection({ iceServers: VCS.Signaling.ICE_SERVERS });
        s._iceRestartCount = 0;
        s._sfuFallbackActive = false;
        VCS.netStats?.start();
        if (s.captionsEnabled) {
            VCS.captions?.startAutoCapture().catch(() => {});
        }

        // Lokale Tracks hinzufügen
        s.localStream.getTracks().forEach(track => s.pc.addTrack(track, s.localStream));
        this._applyVideoBitrateCap();

        // Remote Tracks empfangen
        s.pc.ontrack = (event) => {
            s.remoteStream = event.streams[0];
            const remoteVid = this._view.remoteVideo[0];
            if (remoteVid) {
                remoteVid.srcObject = s.remoteStream;
                // iOS Safari: explizites play() nach srcObject-Zuweisung
                remoteVid.play().catch(() => {});
            }
            // Safari: Jitter-Buffer vergrößern gegen Audio-Stottern
            if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent) && event.track.kind === 'audio') {
                const receiver = s.pc.getReceivers().find(r => r.track === event.track);
                if (receiver && 'playoutDelayHint' in receiver) {
                    receiver.playoutDelayHint = 0.15;
                }
            }
            VCS.audioMonitor?.startRemote(s.remoteStream);
            // Hinweis: seit Toggle-Umstellung 2026-05-10 wird der Remote-
            // Stream nicht mehr durch den Audio-Fader-Web-Audio-Graph
            // geroutet. Original-Stimme spielt direkt aus <video>, der
            // Fader steuert nur noch <video>.muted und den Translated-
            // Audio-Pfad. Kein attachOriginalStream-Aufruf mehr noetig.
        };

        // ICE Candidates senden
        s.pc.onicecandidate = (event) => {
            if (event.candidate) {
                VCS.signaling?.send({
                    type     : 'candidate',
                    from     : s.myId,
                    candidate: event.candidate,
                });
            }
        };

        // Verbindungsstatus überwachen
        s.pc.onconnectionstatechange = () => {
            // Race: pc.close() + s.pc = null kann zwischen Event-Dispatch und
            // Handler-Ausfuehrung passieren (iOS Safari loggt hier sonst null-deref).
            if (!s.pc) {
                return;
            }
            const state = s.pc.connectionState;
            const dot = this._view.connectionDot[0];
            const text = this._view.connectionText[0];
            if (!dot || !text) {
                return;
            }

            switch (state) {
                case 'connected':
                    dot.className = 'd-inline-block w-2 h-2 rounded-full bg-green-500 me-1';
                    text.textContent = t('connected');
                    s._iceRestartCount = 0;
                    this._reportIceConnectionInfo(s.pc);
                    break;
                case 'connecting':
                    dot.className = 'd-inline-block w-2 h-2 rounded-full bg-yellow-500 me-1';
                    text.textContent = t('connecting');
                    break;
                case 'disconnected':
                    dot.className = 'd-inline-block w-2 h-2 rounded-full bg-yellow-500 me-1';
                    text.textContent = t('interrupted');
                    break;
                case 'failed':
                    dot.className = 'd-inline-block w-2 h-2 rounded-full bg-red-500 me-1';
                    text.textContent = t('failed');
                    s._iceRestartCount = (s._iceRestartCount || 0) + 1;
                    console.log('[webrtc] ICE failed, restartCount=' + s._iceRestartCount);
                    if (s._iceRestartCount <= 1) {
                        s.pc.restartIce();
                    } else {
                        // 3x gescheitert → SFU-Fallback
                        this._requestSfuFallback();
                    }
                    break;
                case 'closed':
                    break;
            }
        };

        s.pc.oniceconnectionstatechange = () => {
            if (s.pc && s.pc.iceConnectionState === 'failed') {
                s._iceRestartCount = (s._iceRestartCount || 0) + 1;
                if (s._iceRestartCount <= 1) {
                    s.pc.restartIce();
                } else {
                    this._requestSfuFallback();
                }
            }
        };

        // Guest erstellt Offer
        if (isInitiator) {
            const offer = await s.pc.createOffer();
            const hqOffer = this._setOpusHQ(offer);
            await s.pc.setLocalDescription(hqOffer);
            VCS.signaling?.send({
                type: 'offer',
                from: s.myId,
                sdp : hqOffer,
            });
        }
    }

    // Eingehendes Offer verarbeiten
    async handleOffer(msg) {
        const s = VCS.state;
        if (!s.pc) {
            await this.startCall(false);
        }

        await s.pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        const answer = await s.pc.createAnswer();
        const hqAnswer = this._setOpusHQ(answer);
        await s.pc.setLocalDescription(hqAnswer);

        VCS.signaling?.send({
            type: 'answer',
            from: s.myId,
            sdp : hqAnswer,
        });

        this.flushCandidates();
    }

    // Gespeicherte ICE-Candidates anwenden
    flushCandidates() {
        const s = VCS.state;
        s.candidateQueue.forEach(async (c) => {
            try {
                await s.pc.addIceCandidate(new RTCIceCandidate(c));
            } catch (e) {
                console.warn('ICE-Candidate Fehler:', e);
            }
        });
        s.candidateQueue = [];
    }

    // ICE-Restart: Verbindung neu aushandeln ohne Call zu beenden
    async iceRestart() {
        const s = VCS.state;

        // 1:1-Call
        if (s.pc && s.directCallActive) {
            try {
                const offer = await s.pc.createOffer({ iceRestart: true });
                const hqOffer = this._setOpusHQ(offer);
                await s.pc.setLocalDescription(hqOffer);
                VCS.signaling?.send({ type: 'offer', from: s.myId, sdp: hqOffer });
            } catch (e) {
                console.warn('[webrtc] ICE-Restart fehlgeschlagen:', e);
            }
            return;
        }

        // Gruppen-Call: alle Peers neu verbinden
        if (s.groupCallActive && s.groupCallPeers?.size > 0) {
            for (const [peerId, peer] of s.groupCallPeers) {
                if (!peer.pc) continue;
                try {
                    const offer = await peer.pc.createOffer({ iceRestart: true });
                    const hqOffer = this._setOpusHQ(offer);
                    await peer.pc.setLocalDescription(hqOffer);
                    VCS.signaling?.send({ type: 'group-offer', from: s.myId, to: peerId, sdp: hqOffer });
                } catch (e) {
                    console.warn('[webrtc] Group ICE-Restart fehlgeschlagen für', peerId, e);
                }
            }
        }
    }

    // Call beenden
    endCall(reason) {
        const s = VCS.state;

        VCS.media?._clearCamHardStopTimer();
        s.camOffByButton = false;
        s.camHardStopped = false;
        VCS.audioMonitor?.stopIncomingKnock();
        VCS.audioMonitor?.stopRemote();
        VCS.netStats?.stop();
        VCS.captions?.releasePipeline();
        VCS.captions?.clearTranslated();
        VCS.captions?.clearSelfTranscript();
        // Minuten-Hinweis abraeumen -- sonst steht die Warnung des letzten
        // Gespraechs beim naechsten wieder da.
        VCS.liveCapNotice?.reset();
        s.activeJoinNotification?.close();
        s.activeJoinNotification = null;
        VCS.session?.resetToPreJoin(true);
        s.pendingRegisterOnOpen = false;
        s.directCallActive = false;
        s.directCallPeerName = '';
        s.directCallPeerPhone = '';
        s.guestCallActive = false;
        s.guestAcceptPending = false;
        VCS.session?.setRejoinHint(false);

        // SFU-Fallback schliessen
        if (s._sfuClient) {
            s._sfuClient.close();
            s._sfuClient = null;
        }
        s._sfuFallbackActive = false;
        s._iceRestartCount = 0;

        // PeerConnection schließen
        if (s.pc) {
            s.pc.close();
            s.pc = null;
        }

        // Remote-Stream stoppen
        if (s.remoteStream) {
            s.remoteStream.getTracks().forEach(t => t.stop());
            s.remoteStream = null;
        }
        this._view.remoteVideo.prop('srcObject', null);

        // Lokale Streams stoppen (Kamera + Mikrofon aus)
        VCS.blur?.stopPipeline();
        if (s.localStream && s.localStream !== s.baseStream) {
            s.localStream.getTracks().forEach(tr => tr.stop());
        }
        s.localStream = null;
        if (s.baseStream) {
            s.baseStream.getTracks().forEach(tr => tr.stop());
            s.baseStream = null;
        }

        // Remote-Indikatoren zurücksetzen
        this._view.remoteCamOff.addClass('hidden');
        this._view.remoteMuteIndicator.addClass('hidden');

        // Zurück zum Home
        VCS.nav?.showHomeScreen();
        VCS.captions?.updateFabUI();
        VCS.ui?.showToast(reason, 'info');

        // State zurücksetzen
        s.myRole = null;
        s.peerName = '';
        s.peerLang = null;
        s.remoteMuted = false;
        s.pendingGuestId = null;
        s.candidateQueue = [];

        // WebSocket neu verbinden
        if (s.ws) {
            s.ws.close();
        }
        VCS.signaling?.scheduleReconnect(500);
    }

    // ── SFU-Fallback fuer 1:1-Calls ────────────────────────────

    _requestSfuFallback() {
        const s = VCS.state;
        if (s._sfuFallbackActive || !s.directCallActive) return;
        s._sfuFallbackActive = true;
        console.log('[webrtc] ICE-Restart 3x gescheitert — fordere SFU-Fallback an');
        VCS.signaling?.send({ type: 'sfu-fallback-request', id: s.myId });
    }

    // Test-Methode: SFU-Fallback manuell ausloesen (Browser-Konsole: VCS.webrtc.testSfuFallback())
    testSfuFallback() {
        console.log('[webrtc] TEST: SFU-Fallback manuell ausgeloest');
        this._requestSfuFallback();
    }

    async switchToSfu(roomId) {
        const s = VCS.state;
        if (!s.directCallActive) return;
        s._sfuFallbackActive = true;
        console.log('[webrtc] Wechsel zu SFU-Fallback: room=' + roomId);

        // Alten Remote-Stream leeren (Tracks vom geschlossenen PC sind tot)
        if (s.remoteStream) {
            s.remoteStream.getTracks().forEach(t => s.remoteStream.removeTrack(t));
        }

        // 1. Alte PeerConnection schliessen (aber Streams behalten!)
        if (s.pc) {
            s.pc.ontrack = null;
            s.pc.onicecandidate = null;
            s.pc.onconnectionstatechange = null;
            s.pc.oniceconnectionstatechange = null;
            s.pc.close();
            s.pc = null;
        }

        // 2. Peer als SFU-Peer registrieren (fuer Grid-Kompatibilitaet nicht noetig,
        //    aber SfuClient braucht den Client-Eintrag auf dem Server)

        // 3. SFU-Client erstellen und starten
        s._sfuClient = new VCS.SfuClient();
        try {
            await s._sfuClient.setup();
        } catch (e) {
            console.error('[webrtc] SFU-Fallback Setup fehlgeschlagen:', e);
            s._sfuFallbackActive = false;
            s._sfuClient = null;
            VCS.ui?.showToast(t('connectionFailed'), 'error');
            return;
        }

        // 4. Status-Anzeige aktualisieren
        const dot = this._view.connectionDot[0];
        const text = this._view.connectionText[0];
        if (dot) dot.className = 'd-inline-block w-2 h-2 rounded-full bg-green-500 me-1';
        if (text) text.textContent = t('connectedViaServer');

        VCS.ui?.showToast(t('connectedViaServer'), 'success', 3000);
        console.log('[webrtc] SFU-Fallback aktiv');
    }

    // ── Group Call Methoden ───────────────────────────────────

    async startGroupCall(callId, groupId) {
        const s = VCS.state;
        s.groupCallActive = true;
        s.groupCallId = callId;
        s.groupCallGroupId = groupId;
        s.groupCallPeers = new Map();
        // Group-Captions: in Gruppen-Calls ist plain (kein E2E), daher
        // standardmaessig aktiv. Pro Sprecher laeuft serverseitig ein eigener
        // Hybrid-Strang (Deepgram + OpenAI). Caption-Anzeige am Sprecher-Tile,
        // siehe captions.js _renderToGroupTile().
        s.captionsEnabled = true;
        s.captionPaused = false;

        VCS.audioMonitor?.stopPreview();
        VCS.nav?.showScreen('callScreen');
        VCS.netStats?.start();

        // Caption-UI vorbereiten (Config + Start nach group-call-joined)
        VCS.captions?.updateFabUI();

        // Grid statt Single-Video
        $('#remoteVideoContainer').addClass('hidden');
        $('#groupVideoGrid').removeClass('hidden');
        $('#localVideoWrapper').addClass('hidden');
        this.renderGroupGrid();

        VCS.callControls?.updateMuteButton();
        VCS.callControls?.updateCamButton();

        // Join senden
        VCS.signaling?.send({
            type: 'group-call-join',
            id: s.myId,
            name: s.myName || s.chatMyName,
            room: callId,
            groupId: groupId,
            camOff: s.videoOff,
        });
    }

    async _createGroupPC(peerId, peerName, peerCamOff, isInitiator) {
        const s = VCS.state;
        const pc = new RTCPeerConnection({ iceServers: VCS.Signaling.ICE_SERVERS });

        // Track hinzufügen
        if (s.localStream) {
            s.localStream.getTracks().forEach(track => pc.addTrack(track, s.localStream));
        }

        // Bitrate begrenzen bei Group-Call (max 500kbps Video pro Peer)
        const vSender = pc.getSenders().find(snd => snd.track?.kind === 'video');
        if (vSender) {
            const params = vSender.getParameters();
            if (!params.encodings || !params.encodings.length) params.encodings = [{}];
            params.encodings[0].maxBitrate = 500_000;
            vSender.setParameters(params).catch(() => {});
        }

        const peerEntry = { pc, remoteStream: null, name: peerName, camOff: peerCamOff, muted: false, candidateQueue: [] };
        s.groupCallPeers.set(peerId, peerEntry);

        pc.ontrack = (event) => {
            peerEntry.remoteStream = event.streams[0];
            this.renderGroupGrid();
        };

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                VCS.signaling?.send({ type: 'group-candidate', from: s.myId, to: peerId, candidate: event.candidate });
            }
        };

        let iceRestarts = 0;
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'connected') {
                iceRestarts = 0;
            } else if (pc.connectionState === 'failed') {
                iceRestarts++;
                if (iceRestarts >= 3) {
                    console.log('[webrtc] Group-Peer ' + peerId + ': ICE 3x failed -> SFU-Fallback');
                    this._requestGroupSfuFallback();
                } else {
                    pc.restartIce();
                }
            }
        };

        pc.oniceconnectionstatechange = () => {
            if (pc.iceConnectionState === 'connected') {
                iceRestarts = 0;
            } else if (pc.iceConnectionState === 'failed') {
                iceRestarts++;
                if (iceRestarts >= 3) {
                    console.log('[webrtc] Group-Peer ' + peerId + ': ICE 3x failed -> SFU-Fallback');
                    this._requestGroupSfuFallback();
                } else {
                    pc.restartIce();
                }
            }
        };

        if (isInitiator) {
            const offer = await pc.createOffer();
            const hqOffer = this._setOpusHQ(offer);
            await pc.setLocalDescription(hqOffer);
            VCS.signaling?.send({ type: 'group-offer', from: s.myId, to: peerId, sdp: hqOffer });
        }

        return pc;
    }

    async handleGroupOffer(fromId, sdp) {
        const s = VCS.state;
        let peerEntry = s.groupCallPeers.get(fromId);
        if (!peerEntry || !peerEntry.pc) {
            const name = peerEntry?.name || '?';
            const camOff = peerEntry?.camOff || false;
            await this._createGroupPC(fromId, name, camOff, false);
            peerEntry = s.groupCallPeers.get(fromId);
        }
        await peerEntry.pc.setRemoteDescription(new RTCSessionDescription(sdp));
        const answer = await peerEntry.pc.createAnswer();
        const hqAnswer = this._setOpusHQ(answer);
        await peerEntry.pc.setLocalDescription(hqAnswer);
        VCS.signaling?.send({ type: 'group-answer', from: s.myId, to: fromId, sdp: hqAnswer });
        this._flushGroupCandidates(fromId);
    }

    async handleGroupAnswer(fromId, sdp) {
        const s = VCS.state;
        const peerEntry = s.groupCallPeers.get(fromId);
        if (!peerEntry?.pc) return;
        await peerEntry.pc.setRemoteDescription(new RTCSessionDescription(sdp));
        this._flushGroupCandidates(fromId);
    }

    handleGroupCandidate(fromId, candidate) {
        const s = VCS.state;
        const peerEntry = s.groupCallPeers.get(fromId);
        if (!peerEntry) return;
        if (peerEntry.pc && peerEntry.pc.remoteDescription) {
            peerEntry.pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
        } else {
            peerEntry.candidateQueue.push(candidate);
        }
    }

    _flushGroupCandidates(peerId) {
        const peerEntry = VCS.state.groupCallPeers.get(peerId);
        if (!peerEntry) return;
        for (const c of peerEntry.candidateQueue) {
            peerEntry.pc?.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
        }
        peerEntry.candidateQueue = [];
    }

    // ── Group-Call SFU-Fallback ─────────────────────────────

    _requestGroupSfuFallback() {
        const s = VCS.state;
        if (s._sfuFallbackActive || !s.groupCallActive) return;
        s._sfuFallbackActive = true;
        console.log('[webrtc] Group-Call: ICE gescheitert — fordere SFU-Fallback an');
        VCS.signaling?.send({ type: 'sfu-group-fallback-request', id: s.myId });
    }

    // Test-Methode: Group-SFU-Fallback manuell ausloesen (Konsole: VCS.webrtc.testGroupSfuFallback())
    testGroupSfuFallback() {
        console.log('[webrtc] TEST: Group-SFU-Fallback manuell ausgeloest');
        this._requestGroupSfuFallback();
    }

    async switchGroupToSfu(sfuRoomId) {
        const s = VCS.state;
        if (!s.groupCallActive) return;
        s._sfuFallbackActive = true;
        console.log('[webrtc] Group-Call: Wechsel zu SFU — room=' + sfuRoomId);

        // Alle Mesh-PeerConnections schliessen (Name/camOff/muted behalten)
        for (const [, peerEntry] of s.groupCallPeers) {
            if (peerEntry.pc) {
                peerEntry.pc.ontrack = null;
                peerEntry.pc.onicecandidate = null;
                peerEntry.pc.onconnectionstatechange = null;
                peerEntry.pc.oniceconnectionstatechange = null;
                peerEntry.pc.close();
                peerEntry.pc = null;
            }
            if (peerEntry.remoteStream) {
                peerEntry.remoteStream.getTracks().forEach(t => peerEntry.remoteStream.removeTrack(t));
                peerEntry.remoteStream = null;
            }
            peerEntry.candidateQueue = [];
        }

        // SFU-Client erstellen und starten
        s._sfuClient = new VCS.SfuClient();
        try {
            await s._sfuClient.setup();
        } catch (e) {
            console.error('[webrtc] Group-SFU-Fallback Setup fehlgeschlagen:', e);
            s._sfuFallbackActive = false;
            s._sfuClient = null;
            VCS.ui?.showToast(t('connectionFailed'), 'error');
            return;
        }

        VCS.ui?.showToast(t('connectedViaServer'), 'success', 3000);
        console.log('[webrtc] Group-SFU-Fallback aktiv');
    }

    updateGroupPeerMute(peerId, muted) {
        const peerEntry = VCS.state.groupCallPeers.get(peerId);
        if (peerEntry) { peerEntry.muted = muted; this.renderGroupGrid(); }
    }

    updateGroupPeerCam(peerId, camOff) {
        const peerEntry = VCS.state.groupCallPeers.get(peerId);
        if (peerEntry) { peerEntry.camOff = camOff; this.renderGroupGrid(); }
    }

    removeGroupPeer(peerId) {
        const s = VCS.state;
        const peerEntry = s.groupCallPeers.get(peerId);
        if (!peerEntry) return;
        const name = peerEntry.name;
        // Alte Mesh-PeerConnection schliessen (Fallback)
        if (peerEntry.pc) { try { peerEntry.pc.close(); } catch (_) {} }
        if (peerEntry.remoteStream) { peerEntry.remoteStream.getTracks().forEach(t => t.stop()); }
        // Caption-State des entfernten Peers freigeben (rAF/hideTimer abraeumen)
        VCS.captions?._dropGroupSpeaker?.(peerId);
        s.groupCallPeers.delete(peerId);
        this.renderGroupGrid();
        VCS.ui?.showToast(name + ' ' + t('groupPeerLeft'), 'info');
        if (s.groupCallPeers.size === 0) {
            this.endGroupCall(t('groupCallEnded'));
        }
    }

    endGroupCall(reason) {
        const s = VCS.state;
        VCS.netStats?.stop();

        // SFU-Client schliessen
        if (s._sfuClient) {
            s._sfuClient.close();
            s._sfuClient = null;
        }

        // Alte Mesh-PeerConnections schliessen (Fallback)
        for (const [, peerEntry] of s.groupCallPeers) {
            if (peerEntry.pc) { try { peerEntry.pc.close(); } catch (_) {} }
            if (peerEntry.remoteStream) { peerEntry.remoteStream.getTracks().forEach(t => t.stop()); }
        }
        s.groupCallPeers = new Map();

        VCS.signaling?.send({ type: 'group-leave', id: s.myId });

        // Streams stoppen
        VCS.blur?.stopPipeline();
        if (s.localStream && s.localStream !== s.baseStream) {
            s.localStream.getTracks().forEach(tr => tr.stop());
        }
        s.localStream = null;
        if (s.baseStream) {
            s.baseStream.getTracks().forEach(tr => tr.stop());
            s.baseStream = null;
        }

        // Grid ausblenden, Buttons zurücksetzen
        $('#groupVideoGrid').addClass('hidden').empty();
        $('#remoteVideoContainer').removeClass('hidden');
        $('#localVideoWrapper').removeClass('hidden');

        // Captions stoppen
        VCS.captions?.stopAutoCapture?.();
        VCS.liveCapNotice?.reset();

        s.groupCallActive = false;
        s.groupCallId = null;
        s.groupCallGroupId = null;
        // Bestellung und Symbole gehoeren zu diesem einen Call.
        VCS.callControls?.resetSummary?.();

        VCS.nav?.showHomeScreen();
        if (reason) VCS.ui?.showToast(reason, 'info');

        // WebSocket neu verbinden
        if (s.ws) s.ws.close();
        VCS.signaling?.scheduleReconnect(500);
    }

    renderGroupGrid() {
        const s = VCS.state;
        const grid = document.getElementById('groupVideoGrid');
        if (!grid || !s.groupCallActive) return;

        const peers = [...s.groupCallPeers.entries()];
        const totalTiles = peers.length + 1; // +1 für Self

        grid.className = 'group-video-grid group-video-grid--' + totalTiles;

        // Self-Tile (Caption-Slot fuer Eigen-Transkript optional)
        let html = '<div class="group-video-tile group-video-tile--self" data-peer-id="self">';
        html += '<video autoplay playsinline muted class="group-video-el"></video>';
        // Gespraechszusammenfassung (28.09.): eigene Bestellung = gelber Punkt
        // am eigenen Namen, wie im 1:1 an der Namens-Pill.
        const selfDot = s.callSummaryActive
            ? '<span class="call-summary-dot" title="' + escapeHtml(t('callSummaryActive')) + '"></span>'
            : '';
        html += '<div class="group-video-name">' + escapeHtml(s.myName || s.chatMyName || 'Du') + ' (' + t('you') + ')' + selfDot + '</div>';
        if (s.audioMuted) html += '<div class="group-video-muted-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M1.5 4.5l21 15M11 5V3a1 1 0 0 1 2 0v2a5 5 0 0 1 5 5v1m-4.5 4.24A5 5 0 0 1 7 10V9m12 3a9 9 0 0 1-14.42 4.58M12 19v4"/></svg></div>';
        if (s.videoOff) html += '<div class="group-video-cam-off"><span class="group-video-cam-off-initial">' + (s.myName || s.chatMyName || 'D').charAt(0).toUpperCase() + '</span></div>';
        // Caption-Slot pro Tile: VCS.captions._renderToGroupTile() schreibt hier rein.
        html += '<div class="group-video-caption hidden" aria-live="polite"></div>';
        html += '</div>';

        // Peer-Tiles
        for (const [peerId, peer] of peers) {
            html += '<div class="group-video-tile" data-peer-id="' + peerId + '">';
            html += '<video autoplay playsinline class="group-video-el"></video>';
            // Dieser Teilnehmer laesst zusammenfassen: Dokument-Symbol wie im
            // 1:1, antippbar (Erklaerung als Toast, siehe unten).
            const peerDoc = (s.callSummaryPeerOrderers || []).includes(peerId)
                ? '<span class="call-summary-peer" role="button" tabindex="0" title="' + escapeHtml(t('callSummaryPeerActiveGroup')) + '">'
                  + '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
                  + '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M9 13h6M9 17h4"/></svg></span>'
                : '';
            html += '<div class="group-video-name">' + escapeHtml(peer.name) + peerDoc + '</div>';
            if (peer.muted) html += '<div class="group-video-muted-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M1.5 4.5l21 15M11 5V3a1 1 0 0 1 2 0v2a5 5 0 0 1 5 5v1m-4.5 4.24A5 5 0 0 1 7 10V9m12 3a9 9 0 0 1-14.42 4.58M12 19v4"/></svg></div>';
            if (peer.camOff) html += '<div class="group-video-cam-off"><span class="group-video-cam-off-initial">' + (peer.name || '?').charAt(0).toUpperCase() + '</span></div>';
            // Caption-Slot pro Sprecher-Tile (s.o.).
            html += '<div class="group-video-caption hidden" aria-live="polite"></div>';
            html += '</div>';
        }

        grid.innerHTML = html;

        // title allein sieht man auf dem Handy nicht -- Antippen erklaert es.
        grid.querySelectorAll('.call-summary-peer').forEach((el) => {
            el.addEventListener('click', (e) => {
                e.stopPropagation();
                VCS.ui?.showToast?.(t('callSummaryPeerActiveGroup'), 'info', 5000);
            });
        });

        // Self-Video zuweisen
        const selfVideo = grid.querySelector('.group-video-tile--self video');
        if (selfVideo && s.localStream) {
            selfVideo.srcObject = s.localStream;
            selfVideo.play().catch(() => {});
        }

        // Peer-Videos zuweisen
        for (const [peerId, peer] of peers) {
            const tile = grid.querySelector('[data-peer-id="' + peerId + '"] video');
            if (tile && peer.remoteStream) {
                tile.srcObject = peer.remoteStream;
                tile.play().catch(() => {});
            }
        }
    }

    replaceGroupVideoTrack(newTrack) {
        const s = VCS.state;
        for (const [, peer] of s.groupCallPeers) {
            if (!peer.pc) continue;
            const sender = peer.pc.getSenders().find(snd => snd.track?.kind === 'video');
            if (sender) sender.replaceTrack(newTrack).catch(() => {});
        }
    }

    // Video-Track im PeerConnection ersetzen (1:1)
    replacePeerVideoTrack(nextTrack) {
        const s = VCS.state;
        if (!s.pc || !nextTrack) {
            return;
        }
        const sender = s.pc.getSenders().find(snd => snd.track?.kind === 'video');
        if (sender) {
            sender.replaceTrack(nextTrack).catch(() => {});
        }
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // Nichts zu binden — wird von signaling.js / call-controls.js aufgerufen
    }

    // ── Private Methoden ─────────────────────────────────────

    // Opus-Parameter nach Netzwerkqualität
    _getOpusParams() {
        const nq = VCS.state.networkQuality;
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        if (nq === 'low') {
            return {
                stereo           : '0',
                spropStereo      : '0',
                maxaveragebitrate: '64000',
                maxplaybackrate  : '48000',
                cbr              : '1',
                useinbandfec     : '1',
                ptime            : '40',
            };
        }
        if (nq === 'medium') {
            return {
                stereo           : isSafari ? '0' : '1',
                spropStereo      : isSafari ? '0' : '1',
                maxaveragebitrate: '128000',
                maxplaybackrate  : '48000',
                cbr              : isSafari ? '1' : '0',
                useinbandfec     : '1',
                ptime            : '20',
            };
        }
        return {
            stereo           : '0',
            spropStereo      : '0',
            maxaveragebitrate: '128000',
            maxplaybackrate  : '48000',
            cbr              : isSafari ? '1' : '0',
            useinbandfec     : '1',
            ptime            : '20',
        };
    }

    // SDP manipulieren: Opus-Qualität maximieren
    _setOpusHQ(desc) {
        const op = this._getOpusParams();
        desc.sdp = desc.sdp.replace(/a=fmtp:(\d+) (.*)\r\n/g, (match, payload, params) => {
            let fmtp = params;
            if (!/stereo=/.test(fmtp)) {
                fmtp += `;stereo=${op.stereo};sprop-stereo=${op.spropStereo}`;
            }
            if (!/maxaveragebitrate=/.test(fmtp)) {
                fmtp += `;maxaveragebitrate=${op.maxaveragebitrate}`;
            }
            if (!/maxplaybackrate=/.test(fmtp)) {
                fmtp += `;maxplaybackrate=${op.maxplaybackrate}`;
            }
            if (!/cbr=/.test(fmtp)) {
                fmtp += `;cbr=${op.cbr}`;
            }
            if (!/useinbandfec=/.test(fmtp)) {
                fmtp += `;useinbandfec=${op.useinbandfec}`;
            }
            if (!/ptime=/.test(fmtp)) {
                fmtp += `;ptime=${op.ptime}`;
            }
            return `a=fmtp:${payload} ${fmtp}\r\n`;
        });
        if (!/a=fmtp:\d+/.test(desc.sdp)) {
            desc.sdp = desc.sdp.replace(
                /a=rtpmap:(\d+) opus\/48000\/2\r\n/,
                `a=rtpmap:$1 opus/48000/2\r\na=fmtp:$1 stereo=${op.stereo};sprop-stereo=${op.spropStereo};maxaveragebitrate=${op.maxaveragebitrate};maxplaybackrate=${op.maxplaybackrate};cbr=${op.cbr};useinbandfec=${op.useinbandfec};ptime=${op.ptime}\r\n`
            );
        }
        return desc;
    }

    // Video-Bitrate begrenzen bei schlechter Verbindung
    _applyVideoBitrateCap() {
        const s = VCS.state;
        if (!s.pc || s.networkQuality === 'high') {
            return;
        }
        const sender = s.pc.getSenders().find(snd => snd.track?.kind === 'video');
        if (!sender) {
            return;
        }
        const params = sender.getParameters();
        if (!params.encodings || !params.encodings.length) {
            params.encodings = [{}];
        }
        const maxBitrate = s.networkQuality === 'low' ? 350_000 : 800_000;
        params.encodings[0].maxBitrate = maxBitrate;
        sender.setParameters(params).catch(() => {});
    }

    // ICE-Verbindungsinfo an Server melden
    async _reportIceConnectionInfo(pc) {
        try {
            const stats = await pc.getStats();
            let activeLocalId = null;
            let activeRemoteId = null;
            let rtt = null;

            stats.forEach(report => {
                if (report.type === 'candidate-pair' && (report.state === 'succeeded' || report.nominated)) {
                    activeLocalId = report.localCandidateId;
                    activeRemoteId = report.remoteCandidateId;
                    rtt = report.currentRoundTripTime;
                }
            });

            if (!activeLocalId) return;

            let localType = '?', remoteType = '?', remoteAddr = '?', protocol = '?', turnServer = '';
            stats.forEach(report => {
                if (report.id === activeLocalId && report.type === 'local-candidate') {
                    localType = report.candidateType || '?';
                    protocol = report.protocol || '?';
                    if (report.url) turnServer = report.url;
                }
                if (report.id === activeRemoteId && report.type === 'remote-candidate') {
                    remoteType = report.candidateType || '?';
                    remoteAddr = report.address || report.ip || '?';
                }
            });

            const connType = (localType === 'relay' || remoteType === 'relay') ? 'relay'
                           : (localType === 'srflx' || remoteType === 'srflx') ? 'srflx'
                           : 'host';

            VCS.signaling?.send({
                type: 'ice-connection-info',
                connType,
                localType,
                remoteType,
                remoteAddr,
                protocol,
                turnServer,
                rtt: rtt != null ? Math.round(rtt * 1000) : null
            });
        } catch (e) {
            // Stats nicht verfügbar — ignorieren
        }
    }
};
